import { getMailboxColor, getMailboxTagLabel, MailboxTagColor } from 'src/app/shared/utils/user-color.util';
import { IAccountMailbox, MailboxType } from '../models/account.models';

/** Everything `ui-initials-tag` needs to render a mailbox, plus its hover text. */
export interface IMailboxTag {
  label: string;
  bg: string;
  border: string;
  text: string;
  tooltip: string;
  /** Spoken form of the code — see `ui-initials-tag`'s `ariaLabel`. */
  ariaLabel: string;
}

export interface IMailboxTagOptions {
  /** The user's preference allows the tag on this surface. */
  enabled: boolean;
  /** More than one mailbox is connected, so the tag is not redundant. */
  alwaysVisible: boolean;
  /** Collision-free tint assignment across the connected accounts. */
  colors: Record<number, MailboxTagColor>;
}

/**
 * Builds the recipient-mailbox tag for a thread, or `null` when it must not render.
 *
 * The tag exists to disambiguate: with a single connected mailbox every thread
 * belongs to it, so the tag is noise and is hidden. It appears as soon as a
 * second mailbox is connected (`alwaysVisible`), and always on threads reached
 * through the shared inbox — there you only ever see one invited email, so the
 * mailbox it landed in is never obvious from context.
 *
 * `enabled` is the user's own choice and outranks both rules: opting out in
 * Settings hides the tag everywhere, shared threads included.
 *
 * A mailbox absent from `colors` — a shared thread belongs to someone else's
 * mailbox, which never appears in your accounts — falls back to its hashed tint.
 */
export function buildMailboxTag(
  mailbox: IAccountMailbox | null | undefined,
  { enabled, alwaysVisible, colors }: IMailboxTagOptions
): IMailboxTag | null {
  if (!enabled || !mailbox) return null;
  if (!alwaysVisible && mailbox.type !== MailboxType.SHARED) return null;

  const label = getMailboxTagLabel(mailbox);
  if (!label) return null;

  const color = (mailbox.id != null ? colors[mailbox.id] : undefined) ?? getMailboxColor(mailbox);

  const identity = (mailbox.email || mailbox.name || '').trim();

  return {
    label,
    bg: color.bg,
    border: color.border,
    text: color.text,
    tooltip: identity,
    ariaLabel: identity ? `Mailbox: ${identity}` : label,
  };
}
